import isPast from 'date-fns/isPast';

import {
  AssetDetails,
  AssetDetailsWithStatus,
  PrimaryStatus,
  Scenario,
  ScenarioEnum,
  SecondaryStatus,
  Status,
} from '@/api/asset/asset-schema';

import { getEarningState } from '../asset/assetUtils';

export const mapAssetData = (
  assetData: AssetDetails,
  activeScenario: ScenarioEnum
): AssetDetailsWithStatus => {
  const scenario = assetData.scenarios[activeScenario];
  const mapped: AssetDetailsWithStatus = {
    ...assetData,
    status: getStatus(assetData, scenario),
  };
  return mapped;
};

// For reference this file is based on the status scenarios document seen here: https://app.clickup.com/10607188/v/dc/a3pjm-664/a3pjm-2603

export const mapStatus = (
  primaryStatus: PrimaryStatus,
  assetDetails?: AssetDetails
): Status => {
  const isNotReported = !assetDetails?.reported;
  const isAnalyzing =
    !!assetDetails &&
    isPast(new Date(assetDetails.reporting_date)) &&
    assetDetails.eps.value === undefined;

  const isReported =
    assetDetails?.reported && assetDetails?.eps.value !== undefined;

  if (isAnalyzing) {
    return {
      primary: primaryStatus,
      secondary: SecondaryStatus.Analyzing,
    };
  }

  if (isNotReported) {
    return {
      primary: primaryStatus,
      secondary: SecondaryStatus.NotReported,
    };
  }

  if (isReported) {
    return {
      primary: primaryStatus,
      secondary: SecondaryStatus.Reported,
      tertiary: getEarningState(assetDetails.eps),
    };
  }

  return {
    primary: PrimaryStatus.None,
    secondary: SecondaryStatus.NotReported,
  };
};

export const getStatus = (
  assetDetails?: AssetDetails,
  scenario?: Scenario
): Status => {
  const hasEstimate = assetDetails?.eps.estimate !== undefined;
  const hasHistory = !!scenario?.history?.meta.count;

  // 1 history & estimate
  if (hasHistory && hasEstimate) {
    return mapStatus(PrimaryStatus.Both, assetDetails);
  }

  // 2 no history & estimate
  if (!hasHistory && hasEstimate) {
    return mapStatus(PrimaryStatus.OnlyEstimate, assetDetails);
  }

  // 3 history & no estimate
  if (hasHistory && !hasEstimate) {
    return mapStatus(PrimaryStatus.OnlyHistory, assetDetails);
  }

  // 4 no history no estimate
  if (!hasHistory && !hasEstimate) {
    return mapStatus(PrimaryStatus.None, assetDetails);
  }

  return {
    primary: PrimaryStatus.None,
    secondary: SecondaryStatus.NotReported,
  };
};
